import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { toast } from 'sonner';
import { Users, UserPlus, X, Mic, MicOff, Video, VideoOff, Crown, ChevronDown, ChevronUp, Shield, Lock, Globe, UserCheck } from 'lucide-react';

const JOIN_MODES = [
  { value: 'open', label: 'Anyone', icon: Globe },
  { value: 'followers', label: 'Followers Only', icon: UserCheck },
  { value: 'invite_only', label: 'Invite Only', icon: UserPlus },
  { value: 'locked', label: 'Locked', icon: Lock },
];

// ─── Host Controls Panel ───
export function HostControlsPanel({ streamId, isHost }) {
  const { getAuthHeaders } = useAuth();
  const [participants, setParticipants] = useState({ host: null, guests: [], count: 1, max: 10, join_mode: 'open' });
  const [joinRequests, setJoinRequests] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [showModeMenu, setShowModeMenu] = useState(false);

  const fetchParticipants = useCallback(async () => {
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/participants`);
      if (res.ok) setParticipants(await res.json());
    } catch {}
  }, [streamId]);

  const fetchRequests = useCallback(async () => {
    if (!isHost) return;
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/join-requests`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setJoinRequests(data.requests || []);
      }
    } catch {}
  }, [streamId, isHost, getAuthHeaders]);

  useEffect(() => {
    fetchParticipants();
    fetchRequests();
    const interval = setInterval(() => {
      fetchParticipants();
      fetchRequests();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchParticipants, fetchRequests]);

  const approveJoin = async (userId) => {
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/approve-join/${userId}`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Guest approved');
        fetchParticipants();
        fetchRequests();
      } else {
        const d = await res.json().catch(() => ({}));
        toast.error(d?.detail || 'Failed to approve');
      }
    } catch { toast.error('Failed to approve'); }
  };

  const declineJoin = async (userId) => {
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/decline-join/${userId}`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.info('Request declined');
        fetchRequests();
      }
    } catch {}
  };

  const removeGuest = async (userId) => {
    if (!window.confirm('Remove this guest from the live?')) return;
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/remove-guest/${userId}`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Guest removed');
        fetchParticipants();
      }
    } catch { toast.error('Failed to remove'); }
  };

  const updateJoinMode = async (mode) => {
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/join-mode`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ join_mode: mode })
      });
      if (res.ok) {
        setParticipants(p => ({ ...p, join_mode: mode }));
        setShowModeMenu(false);
        toast.success(`Join mode: ${mode}`);
      }
    } catch {}
  };

  const currentMode = JOIN_MODES.find(m => m.value === participants.join_mode) || JOIN_MODES[0];

  return (
    <Card className="glass-card border-border overflow-hidden" data-testid="host-controls-panel">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
        data-testid="toggle-participants-panel"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold">Participants</span>
          <Badge variant="secondary" className="text-xs" data-testid="participant-count">
            {participants.count}/{participants.max}
          </Badge>
          {joinRequests.length > 0 && (
            <Badge className="bg-red-500 text-white text-xs animate-pulse" data-testid="join-request-badge">
              {joinRequests.length}
            </Badge>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="p-3 pt-0 space-y-3">
          {/* Join Mode (host only) */}
          {isHost && (
            <div className="relative">
              <button
                onClick={() => setShowModeMenu(!showModeMenu)}
                className="flex items-center gap-2 text-xs px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 transition-colors w-full"
                data-testid="join-mode-selector"
              >
                <currentMode.icon className="w-3 h-3" />
                <span>Join: {currentMode.label}</span>
                <ChevronDown className="w-3 h-3 ml-auto" />
              </button>
              {showModeMenu && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-background border border-border rounded-md shadow-lg z-50">
                  {JOIN_MODES.map(m => (
                    <button
                      key={m.value}
                      onClick={() => updateJoinMode(m.value)}
                      className={`flex items-center gap-2 w-full text-left text-xs px-3 py-2 hover:bg-white/10 transition-colors ${m.value === participants.join_mode ? 'text-primary' : ''}`}
                      data-testid={`join-mode-${m.value}`}
                    >
                      <m.icon className="w-3 h-3" />
                      {m.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Join Requests (host only) */}
          {isHost && joinRequests.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-amber-400">Join Requests</p>
              {joinRequests.map(req => (
                <div key={req.user_id} className="flex items-center gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20" data-testid={`join-request-${req.user_id}`}>
                  <img src={req.avatar || '/default-avatar.png'} alt="" className="w-7 h-7 rounded-full object-cover bg-muted" onError={e => { e.target.src = '/default-avatar.png'; }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{req.name}</p>
                    <p className="text-[10px] text-muted-foreground">@{req.handle}</p>
                  </div>
                  <Button size="sm" className="h-6 text-xs px-2" onClick={() => approveJoin(req.user_id)} data-testid={`approve-${req.user_id}`}>
                    Accept
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 text-xs px-1" onClick={() => declineJoin(req.user_id)} data-testid={`decline-${req.user_id}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Host */}
          {participants.host && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-primary/10 border border-primary/20">
              <img src={participants.host.avatar || '/default-avatar.png'} alt="" className="w-7 h-7 rounded-full object-cover bg-muted" onError={e => { e.target.src = '/default-avatar.png'; }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{participants.host.name}</p>
              </div>
              <Badge className="bg-primary/20 text-primary text-[10px]">
                <Crown className="w-2.5 h-2.5 mr-0.5" /> Host
              </Badge>
            </div>
          )}

          {/* Guests */}
          {participants.guests.map(g => (
            <div key={g.user_id} className="flex items-center gap-2 p-2 rounded-md bg-white/5" data-testid={`guest-${g.user_id}`}>
              <img src={g.avatar || '/default-avatar.png'} alt="" className="w-7 h-7 rounded-full object-cover bg-muted" onError={e => { e.target.src = '/default-avatar.png'; }} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{g.name}</p>
              </div>
              <Badge variant="outline" className="text-[10px]">Guest</Badge>
              {isHost && (
                <button onClick={() => removeGuest(g.user_id)} className="text-red-400 hover:text-red-300 transition-colors" data-testid={`remove-guest-${g.user_id}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}

          {participants.count === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No guests yet</p>
          )}
        </div>
      )}
    </Card>
  );
}


// ─── Viewer Join Button ───
export function ViewerJoinButton({ streamId }) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('viewer'); // viewer, pending, guest, invited
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API}/live/streams/${streamId}/my-status`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setStatus(data.status);
        }
      } catch {}
    };
    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [streamId, isAuthenticated, getAuthHeaders]);

  const requestJoin = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/request-join`, {
        method: 'POST', headers: getAuthHeaders()
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('pending');
        toast.success('Join request sent!');
      } else {
        toast.error(data?.detail || 'Failed to request');
      }
    } catch { toast.error('Failed to send request'); }
    setLoading(false);
  };

  const acceptInvite = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/live/streams/${streamId}/accept-invite`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (res.ok) {
        setStatus('guest');
        toast.success('You joined the live!');
        // Reload page to get token
        window.location.reload();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data?.detail || 'Failed to accept');
      }
    } catch { toast.error('Failed to accept invite'); }
    setLoading(false);
  };

  if (!isAuthenticated) return null;
  if (status === 'host' || status === 'guest') return null;

  if (status === 'invited') {
    return (
      <Button
        onClick={acceptInvite}
        disabled={loading}
        className="bg-green-600 hover:bg-green-700 text-white gap-2"
        data-testid="accept-invite-btn"
      >
        <UserPlus className="w-4 h-4" />
        {loading ? 'Joining...' : 'Accept Invite — Join Live'}
      </Button>
    );
  }

  if (status === 'pending') {
    return (
      <Button disabled variant="outline" className="gap-2 opacity-70" data-testid="pending-request-btn">
        <Users className="w-4 h-4" />
        Request Sent — Waiting...
      </Button>
    );
  }

  return (
    <Button
      onClick={requestJoin}
      disabled={loading}
      variant="outline"
      className="gap-2 hover:bg-primary/10"
      data-testid="request-join-btn"
    >
      <UserPlus className="w-4 h-4" />
      {loading ? 'Requesting...' : 'Request to Join'}
    </Button>
  );
}


// ─── Guest Controls Bar ───
export function GuestControlsBar({ streamId, onLeave }) {
  const { getAuthHeaders } = useAuth();
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);

  const leaveLive = async () => {
    try {
      await fetch(`${API}/live/streams/${streamId}/leave`, {
        method: 'POST', headers: getAuthHeaders()
      });
      toast.info('You left the live');
      if (onLeave) onLeave();
    } catch {
      toast.error('Failed to leave');
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid="guest-controls-bar">
      <Button
        size="sm"
        variant={muted ? 'destructive' : 'outline'}
        onClick={() => setMuted(!muted)}
        className="h-8 w-8 p-0"
        data-testid="guest-mute-btn"
      >
        {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
      </Button>
      <Button
        size="sm"
        variant={camOff ? 'destructive' : 'outline'}
        onClick={() => setCamOff(!camOff)}
        className="h-8 w-8 p-0"
        data-testid="guest-camera-btn"
      >
        {camOff ? <VideoOff className="w-4 h-4" /> : <Video className="w-4 h-4" />}
      </Button>
      <Button
        size="sm"
        variant="destructive"
        onClick={leaveLive}
        className="h-8 gap-1 text-xs"
        data-testid="guest-leave-btn"
      >
        <X className="w-3 h-3" /> Leave
      </Button>
    </div>
  );
}


// ─── Adaptive Live Grid Layout ───
export function LiveGridLayout({ participantCount, children }) {
  const getGridClass = () => {
    if (participantCount <= 1) return 'grid-cols-1';
    if (participantCount === 2) return 'grid-cols-2';
    if (participantCount <= 4) return 'grid-cols-2 grid-rows-2';
    if (participantCount <= 6) return 'grid-cols-3 grid-rows-2';
    return 'grid-cols-4'; // 7-10
  };

  return (
    <div className={`grid ${getGridClass()} gap-1 w-full h-full`} data-testid="live-grid-layout">
      {children}
    </div>
  );
}
